<template>
  <vue-final-modal name="bundleProductOptions" v-bind="$attrs" v-slot="{ close }"
    classes="vfm-container vfm-product" v-on="$listeners" content-class="modalBoxShadow">

    <div class="product_container scroll">
      <div class="product_content product_content_full_width">
        <div class="product_content_data scroll">
          <div class="insidePadding">
            <div class="data__header">
              <h4 class="font-weight-bold">{{ product.name }} - Options</h4>
              <button @click="close">
                <i class="material-icons">close</i>
              </button>
            </div>
            <span class="description">{{ product.description }}</span>
          </div>

          <div class="productLine"></div>

          <product-options ref="optionSelector" :product="product" v-model="selectedOptions" :key="componentKey" />
        </div>

        <div class="product_content_footer d-none d-lg-flex">
          <button class="product_cta" @click="confirmOptions(close)" :disabled="isLoading">
            <loader v-if="isLoading"></loader>
            <div v-if="!isLoading">
              Confirmer les options<span v-if="totalSupplementPrice > 0"> ({{ formattedSupplementPrice }})</span>
            </div>
          </button>
        </div>
      </div>
    </div>

    <div class="product_content_footer d-lg-none">
      <button class="product_cta" @click="confirmOptions(close)" :disabled="isLoading">
        <loader v-if="isLoading"></loader>
        <div v-if="!isLoading">
          Confirmer les options<span v-if="totalSupplementPrice > 0"> ({{ formattedSupplementPrice }})</span>
        </div>
      </button>
    </div>
  </vue-final-modal>
</template>

<script>
import { $vfm } from 'vue-final-modal';
import ProductOptions from '@/components/Product/ProductOptions.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'BundleProductOptions',
  components: { ProductOptions },
  props: {
    product: {
      type: Object,
      required: true
    },
    bundlePart: {
      type: Object,
      required: true
    },
    bundlePartItem: {
      type: Object,
      required: true
    },
    defaultModifiers: {
      type: Array,
      default: () => []
    },
    onConfirm: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      selectedOptions: [],
      isLoading: false,
      componentKey: 0
    }
  },
  computed: {
    ...mapGetters(['cart']),

    totalSupplementPrice() {
      if (!this.selectedOptions || this.selectedOptions.length === 0) return 0;

      return this.selectedOptions.reduce((total, option) => {
        return total + option.values.reduce((optionTotal, value) => {
          return optionTotal + (value.price * (value.quantity || 1));
        }, 0);
      }, 0);
    },

    formattedSupplementPrice() {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(this.totalSupplementPrice / 100);
    }
  },
  methods: {
    confirmOptions(close) {
      if (this.$refs.optionSelector.validateFields()) {
        this.isLoading = true;

        // Process selected options to match expected format
        const processedOptions = this.selectedOptions.map(option => ({
          id: option.id,
          values: option.values.map(value => ({
            id: value.id,
            quantity: value.quantity || 1,
            checked: value.checked,
            price: value.price
          }))
        }));

        // Emit event with processed options
        this.$emit('confirm', processedOptions);

        this.isLoading = false;
        if (close) {
          close();
        }
      } else {
        // Scroll to first error
        const errorOptionIds = this.$refs.optionSelector.getOptionErrorIds();
        if (errorOptionIds.length > 0) {
          const errorElement = document.getElementById('option-' + errorOptionIds[0]);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
          }
        }
      }
    },

    formatProductPrice() {
      let price = 0;
      if (this.cart.type === 'delivery') {
        price = this.product.priceDelivery ? this.product.priceDelivery.value : 0;
      } else if (this.cart.type === 'pickup') {
        price = this.product.pricePickup ? this.product.pricePickup.value : 0;
      } else if (this.cart.type === 'on_site' || this.cart.type === 'on_site_counter') {
        price = this.product.priceOnsite ? this.product.priceOnsite.value : 0;
      }

      // Add supplement prices from bundle part and bundle part item
      price += (this.bundlePart.supplementPrice ? this.bundlePart.supplementPrice.value : 0);
      price += (this.bundlePartItem.supplementPrice ? this.bundlePartItem.supplementPrice.value : 0);

      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(price / 100);
    },

    initDefaultModifiers() {
      if (this.defaultModifiers && this.defaultModifiers.length > 0 && this.product && this.product.options) {
        // Set selectedOptions for v-model
        this.selectedOptions = JSON.parse(JSON.stringify(this.defaultModifiers));

        // Apply default modifiers directly to product options
        this.defaultModifiers.forEach(modifier => {
          const option = this.product.options.find(opt => opt.id === modifier.id);

          if (option && modifier.values) {
            modifier.values.forEach(selectedValue => {
              const value = option.values.find(val => val.id === selectedValue.id);
              if (value) {
                value.checked = selectedValue.checked !== false;
                value.quantity = selectedValue.quantity || 1;
              }
            });
          }
        });

        // Force ProductOptions component to re-mount with new data
        this.componentKey += 1;
      }
    }
  },
  mounted() {
    // Apply default modifiers after component is fully mounted
    this.$nextTick(() => {
      this.initDefaultModifiers();
    });

    // Handle back button for mobile
    document.addEventListener("backbutton", () => {
      $vfm.hide('bundleProductOptions');
    }, false);
  }
}
</script>

<style lang="scss">
// Reuse the same styles from AddProduct modal
// The styles are already defined globally

.product_content_full_width {
  width: 100% !important;
  flex: 1 !important;
}

// Remove side margins/padding for full width
.vfm-product .product_container .product_content_full_width {
  max-width: none !important;
}
</style>
